import * as React from "react";
import { FormControl, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";
import { PhoneNumber, useCreatePhoneNumber, useUpdatePhoneNumber } from "@/hooks/api/use-phone-numbers";

export function CreatePhoneNumber({
	phoneNumber,
	isCreateDrawerOpen,
	onCreateDrawerClose,
}: {
	phoneNumber: PhoneNumber | null;
	isCreateDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
}): React.JSX.Element {
  const createPhoneNumberMutation = useCreatePhoneNumber();
	const updatePhoneNumberMutation = useUpdatePhoneNumber();

	const isEdit = !!phoneNumber?.id;

	const methods = useForm<any>({
		defaultValues: {
			concurrencyLimit: phoneNumber?.concurrencyLimit || '',
      enabled: phoneNumber?.enabled || false,
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setError,
		formState: { isSubmitting },
	} = methods;

	React.useLayoutEffect(() => {
		reset({
			concurrencyLimit: phoneNumber?.concurrencyLimit || '',
      enabled: phoneNumber?.enabled || false,
		});
	}, [phoneNumber, reset, isCreateDrawerOpen]);

	const onSubmitForm = async (data: any) => {
    const formattedData = {
      concurrencyLimit: Number(data.concurrencyLimit) || '',
      enabled: data.enabled,
    };
		try {
			await (isEdit && phoneNumber?.id
				? updatePhoneNumberMutation.mutateAsync({ id: phoneNumber.id, data: formattedData })
				: createPhoneNumberMutation.mutateAsync({ data: formattedData }));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Phone Number" : "Create Phone Number"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={
					isSubmitting || createPhoneNumberMutation.isPending || updatePhoneNumberMutation.isPending
				}
				submitButtonText={isEdit ? "Update Phone Number" : "Create Phone Number"}
        width="420px"
			>
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="concurrencyLimit"
								rules={{
									required: "Concurrency limit is required",
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel>Concurrency Limit</InputLabel>
										<OutlinedInput {...field} placeholder="Enter concurrency limit" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

            <Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<DynamicField
								control={control}
								type={DynamicFieldType.BOOLEAN}
								name="enabled"
								label="Enabled"
								dynamicField={{
									name: "enabled",
									type: DynamicFieldType.BOOLEAN,
									label: "Enabled",
								}}
							/>
						</Grid>
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
